import {
  AssigneeWarning,
  AssignmentTypeEnum,
  AvailabilityDto,
  CaseModel,
  CaseTypeEnum,
  TranslationLookups,
  UserModel,
} from 'api/model';
import useUsers, { useRefreshUsersCall } from 'contexts/basicData/useUsers';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  getUserPropsIconArray,
  userPropsArray,
} from 'pages/Employees/filterableUserFields';
import SearchableSelect, {
  SearchableSelectItem,
  SelectOption,
} from './inputs/SearchableSelect';
import UserModalLink from './UserModalLink';
import TextButton from './inputs/TextButton';
import AvailabilityFormModal from 'pages/Employees/Availability/AvailabilityFormModal';
import useModalStack from 'contexts/modal/useModalStack';
import { Badge } from 'pages/Employees/Availability';
import { DropdownPosition } from './Dropdown';
import { useAssignmentSortFieldTestersForNewAssignment } from 'api/assignment/assignment';
import { userNamesExcemptFromEstimationValidation } from 'constants/AppConstants';
import TooltipInfoWarning from './TooltipInfoWarning';
import useMe from 'contexts/authentication/useMe';
import { DefaultWebAdminPolicy, isAuthorized } from 'constants/Roles';
import AvailabilityStatusBadge from 'pages/Employees/Availability/AvailabilityStatusIcon';
import AbsenceReasonIcon from 'pages/Employees/Availability/AbsenceReasonIcon';
import AvailableDaysPill from 'pages/Employees/Availability/AvailableDaysPill';
import useTranslations from 'contexts/basicData/useTranslations';
import RunnerOverrideReasonModal from './RunnerOverrideReasonModal';

export const PrioritizedUserPickerClassName = 'PrioritizedUserPicker';

// "Deltid, procent" från personalkortet — bara information, bredvid veckans markerade dagar.
const PartTimePercentage = styled.span`
  font-size: 0.85em;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const StyledSearchableSelect = styled(SearchableSelect)`
  .${PrioritizedUserPickerClassName} {
    /* Your styles for PrioritizedUserPicker within StyledSearchableSelect go here */
    max-height: 300px !important;
  }
` as typeof SearchableSelect;

const ItemsWrapperStyle = css`
  display: grid;
  grid-template-columns: max-content 1fr max-content max-content max-content;
  grid-auto-flow: row;
  .grid-row:hover ~ .grid-row {
    background-color: yellow;
  }
`;

const EmojiWrapper = styled.div<{ amount: number }>`
  ${({ amount }) => css`
    display: grid;
    grid-template-columns: repeat(${amount}, 1fr);
    grid-auto-flow: row;
  `}
`;

const StyledSelectItemRow = styled.div<{
  selected?: boolean;
  selectable?: boolean;
}>`
  display: contents; /* to allow hover on the whole row (acts like a div but inherits the grid properties) */
  ${({ selected, theme }) =>
    selected &&
    css`
      & > button {
        background: ${theme.colors.background.selection};
        color: ${theme.colors.foreground.selection};
      }
    `}
  &:hover > button {
    background-color: ${({ theme }) => theme.colors.background.selection};
  }

  ${({ selectable }) =>
    selectable &&
    css`
      cursor: pointer;
      & > button {
        cursor: pointer;
      }
    `}
`;

const StyledSelectItem = styled(SearchableSelectItem)<{
  isNoneItem?: boolean;
  isAvailable: boolean;
  isFiller?: boolean;
}>`
  width: 100%;

  ${({ isNoneItem }) =>
    isNoneItem &&
    css`
      font-style: italic;
    `}
  ${({ isFiller }) =>
    isFiller &&
    css`
      font-style: normal !important;
      font-weight: bold;
    `}

  ${({ isAvailable }) =>
    !isAvailable
      ? `
    font-style: italic;
    color: grey!important;
  `
      : `
    font-style: normal;
    color: black!important;`}
  

  ${({ isAvailable, theme }) =>
    css`
      background-color: ${theme.colors.background.selection}!important
      color: ${
        isAvailable
          ? theme.colors.foreground.primary
          : theme.colors.foreground.secondary
      }`};
`;

const StyledUserModalLink = styled(UserModalLink)`
  margin-left: 0.5rem !important;
`;

interface ExtendedUserModel {
  isIllicit?: boolean;
  isAvailable: boolean;
  isFiller?: boolean;
  user?: UserModel;
  availability?: AvailabilityDto;
  lastDerivedEstimatedEndTime?: Date;
  warnings?: AssigneeWarning[];
  warningDetails?: string[];
  /** Behovsanställda: markerat tillgängliga dagar mån–sön i uppdragets vecka. */
  availableDaysInWeek?: number | null;
}

const isSystemUser = (user: UserModel) => {
  return userNamesExcemptFromEstimationValidation.includes(user.name);
};

interface OptionRowProps {
  rowKey: string;
  option: SelectOption<number | null, ExtendedUserModel | null>;
  onClick: () => void;
  isSelected: boolean;
  isAdmin: boolean;
  availabilityStatuses: TranslationLookups['availabilityStatuses'];
  availabilityStatusColorCodes: TranslationLookups['availabilityStatusColorCodes'];
  absenceReasons: TranslationLookups['absenceReasons'];
  absenceStatuses: TranslationLookups['absenceStatuses'];
  onOpenAvailabilityModal: (
    option: SelectOption<number | null, ExtendedUserModel | null>
  ) => void;
}

const OptionRow = memo<OptionRowProps>(
  ({
    rowKey,
    option,
    onClick,
    isSelected,
    isAdmin,
    availabilityStatuses,
    availabilityStatusColorCodes,
    absenceReasons,
    absenceStatuses,
    onOpenAvailabilityModal,
  }) => {
    return (
      <StyledSelectItemRow
        key={`${rowKey}_row`}
        selectable={
          !!(
            option.value === null ||
            (option.extra?.user && option.extra?.isAvailable)
          )
        }
        selected={isSelected}
      >
        <StyledSelectItem
          isAvailable={option.extra?.isAvailable ?? false}
          isFiller={option.extra?.isFiller}
          isNoneItem={option.value === null}
          key={`${rowKey}_label`}
          onClick={onClick}
        >
          <span>
            {option.extra?.warnings && option.extra.warnings.length > 0 && (
              <TooltipInfoWarning
                warnings={option.extra.warnings}
                details={option.extra.warningDetails}
                showOnlyTooltip
                left={20}
                top={-10}
              />
            )}
            {option.label}
            {option.extra?.user && !isSystemUser(option.extra.user) && (
              <span
                style={{
                  float: 'right',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {option.extra.availability?.scheduleStatus !== undefined && (
                  <AvailabilityStatusBadge
                    availabilityStatus={
                      option.extra.availability.scheduleStatus
                    }
                    availabilityStatuses={availabilityStatuses}
                    availabilityStatusColorCodes={availabilityStatusColorCodes}
                  />
                )}
                {option.extra.availability?.absenceStatus !== undefined &&
                  option.extra.availability?.absenceStatus !== null && (
                    <AbsenceReasonIcon
                      absenceReason={option.extra.availability.absenceReason}
                      absenceStatus={option.extra.availability.absenceStatus}
                      absenceReasons={absenceReasons}
                      absenceStatuses={absenceStatuses}
                    />
                  )}
                <span title="Tillgänglighet">
                  {option.extra.availability?.startString && (
                    <span>{option.extra.availability.startString}</span>
                  )}
                  -
                  {option.extra.availability?.endString && (
                    <span>{option.extra.availability.endString}</span>
                  )}
                </span>
                {!option.extra.user.isFullTime &&
                  option.extra.user.partTimePercentage !== undefined &&
                  option.extra.user.partTimePercentage !== null && (
                    <PartTimePercentage
                      title={`Deltid ${option.extra.user.partTimePercentage} %`}
                    >
                      {`${option.extra.user.partTimePercentage} %`}
                    </PartTimePercentage>
                  )}
                {option.extra.availableDaysInWeek !== undefined &&
                  option.extra.availableDaysInWeek !== null && (
                    <AvailableDaysPill
                      days={option.extra.availableDaysInWeek}
                      title={`Markerat sig tillgänglig ${option.extra.availableDaysInWeek} av 7 dagar uppdragets vecka (mån–sön)`}
                    />
                  )}
              </span>
            )}
          </span>
        </StyledSelectItem>
        <StyledSelectItem
          isAvailable={option.extra?.isAvailable ?? false}
          key={`${rowKey}_description`}
          onClick={onClick}
        >
          {option.description}
        </StyledSelectItem>
        <StyledSelectItem
          isAvailable={option.extra?.isAvailable ?? false}
          key={`${rowKey}_city`}
          onClick={onClick}
        >
          {`${option.extra?.user?.city ?? ''} ${
            option.extra?.user?.zipCodeLocalArea
              ? `(${option.extra.user?.zipCodeLocalArea.name})`
              : ''
          }`}
        </StyledSelectItem>
        <StyledSelectItem
          isAvailable={option.extra?.isAvailable ?? false}
          key={`${rowKey}_link`}
        >
          {option.extra?.user && isAdmin ? (
            <TextButton onClick={() => onOpenAvailabilityModal(option)}>
              <Badge>
                {option.extra?.availability?.bookedAssignments ?? 0}
              </Badge>
            </TextButton>
          ) : (
            <div />
          )}
        </StyledSelectItem>
        <StyledSelectItem
          isAvailable={option.extra?.isAvailable ?? false}
          key={`${rowKey}_avail_link`}
        >
          {option.extra?.user &&
          !isSystemUser(option.extra?.user) &&
          isAdmin ? (
            <StyledUserModalLink userId={option.extra.user.userID} />
          ) : (
            <div />
          )}
        </StyledSelectItem>
      </StyledSelectItemRow>
    );
  }
);

interface PrioritizedUserPickerProps {
  autoFocus?: boolean;
  caseModel: Partial<CaseModel>;
  disabled?: boolean;
  dropdownPosition?: DropdownPosition;
  selectedUserId: number | null;
  bookedTo?: string;
  estimatedStartTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  estimatedDuration: number;
  assignmentType: AssignmentTypeEnum;
  onUserPicked(
    userModel: UserModel | null,
    breaksDrivingPolicy?: boolean,
    /** Satt när TL gått förbi runnerspärren i nödfall — skickas som runnerCustomerLegOverrideReason. */
    runnerOverrideReason?: string
  ): void;
  fromZipCode?: string;
  onStateChange?(isOpen: boolean): void;
  assignmentId: number;
  requireExistingAssignmentToSelectFT?: boolean;
  estimatedLoadingTime?: number;
  estimatedUnloadingTime?: number;
  /** Elbils-laddningsbuffert (ms) som reserveras i körpolicy-/tillgänglighetskollen vid planering. */
  electricCarBufferMs?: number;
  /**
   * Får TL välja en runner till ett uppdrag med kundkontakt mot en nödfallsmotivering? Kräver att
   * mottagaren skickar motiveringen vidare till servern. Av där motiveringen inte kan sparas
   * (t.ex. ruttplaneringens bytesmodal, som har en hård spärr).
   */
  allowRunnerOverride?: boolean;
}

const PrioritizedCaseAssignmentUserPicker: FC<PrioritizedUserPickerProps> = ({
  autoFocus,
  caseModel,
  dropdownPosition,
  disabled,
  selectedUserId,
  bookedTo,
  estimatedStartTime,
  estimatedDuration,
  actualStartTime,
  actualEndTime,
  assignmentType,
  fromZipCode,
  onUserPicked,
  onStateChange,
  assignmentId,
  requireExistingAssignmentToSelectFT = true,
  estimatedLoadingTime,
  estimatedUnloadingTime,
  electricCarBufferMs,
  allowRunnerOverride = false,
}) => {
  const me = useMe();
  const isAdmin = me !== null && isAuthorized(DefaultWebAdminPolicy.allow, DefaultWebAdminPolicy.deny, me.roles);
  const {
    availabilityStatuses,
    availabilityStatusColorCodes,
    absenceReasons,
    absenceStatuses,
  } = useTranslations();

  const AlwaysUnallowedAssigneeWarnings: AssigneeWarning[] = useMemo(() => {
    const warnings: AssigneeWarning[] = [
      AssigneeWarning.Overlapping,
      AssigneeWarning.CannotDriveManual,
      AssigneeWarning.MissingDriverLicenseBE,
      AssigneeWarning.MissingDriverLicenseC,
      // Går bara att passera via nödfallsdialogen nedan, aldrig med ett vanligt klick.
      AssigneeWarning.RunnerCustomerContact,
    ];
    if (!me?.isAllowedToOverrideAssigneeWarnings) {
      warnings.push(
        ...[
          AssigneeWarning.IsNotAvailable,
          AssigneeWarning.NotConsecutiveHoursOfRestThisWeek,
          AssigneeWarning.NotConsecutiveHoursOfRestLast24Hours,
          AssigneeWarning.NotConsecutiveHoursOfRestLast7Days,
          AssigneeWarning.NotWithinAllowedDrivingHours,
        ]
      );
    }
    return warnings;
  }, [me?.isAllowedToOverrideAssigneeWarnings]);

  const AllowedAssigneeWarningsOnlyIfInThePast: AssigneeWarning[] =
    useMemo(() => {
      const warnings: AssigneeWarning[] = [
        AssigneeWarning.IsOnlyAvailableForFurtherTransport,
      ];
      if (me?.isAllowedToOverrideAssigneeWarnings) {
        warnings.push(AssigneeWarning.IsNotAvailable);
      }
      return warnings;
    }, [me?.isAllowedToOverrideAssigneeWarnings]);

  const isPastAssignment = useMemo(() => {
    if (!actualEndTime) {
      return false;
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return new Date(actualEndTime) < yesterday;
  }, [actualEndTime]);
  const modalStack = useModalStack();
  const refreshUsersCall = useRefreshUsersCall();

  const [pickerIsOpen, setPickerIsOpen] = useState(false);
  useEffect(() => {
    if (onStateChange) {
      onStateChange(pickerIsOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerIsOpen]);
  const [options, setOptions] = useState<
    SelectOption<number | null, ExtendedUserModel | null>[]
  >([]);
  // const { state: caseState } = useDetailedCaseDataForm();
  const users = useUsers();

  useEffect(() => {
    if (Object.keys(users).length === 0) {
      refreshUsersCall.run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const {
    caseTypeID: caseType,
    vehicleGearBox,
    isElectric,
    isPremiumCar,
    isTrailer,
    isTruck,
  } = caseModel;

  const {
    data: filteredUsersResponse,
    refetch,
    isRefetching,
  } = useAssignmentSortFieldTestersForNewAssignment(
    {
      assignmentId: assignmentId === -1 ? undefined : assignmentId,
      bookedTo: bookedTo ? new Date(bookedTo) : undefined,
      estimatedStartTime: estimatedStartTime
        ? new Date(estimatedStartTime)
        : undefined,
      actualStartTime: actualStartTime ? new Date(actualStartTime) : undefined,
      actualEndTime: actualEndTime ? new Date(actualEndTime) : undefined,
      estimatedDuration: Number.isNaN(estimatedDuration)
        ? 0
        : estimatedDuration,
      caseType,
      assignmentType,
      vehicleGearBox: vehicleGearBox === true,
      isElectric: isElectric === true,
      isPremiumCar: isPremiumCar === true,
      isTrailer: isTrailer === true,
      isTruck: isTruck === true,
      startZipCode: fromZipCode,
      estimatedLoadingTime,
      estimatedUnloadingTime,
      electricCarBufferMs,
    },
    {
      query: {
        enabled: pickerIsOpen && !!bookedTo,
        staleTime: 0,
      },
    }
  );

  // get filtered users from backend when the picker opens
  useEffect(() => {
    if (pickerIsOpen) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerIsOpen]);

  // update options when settings change
  const updateOptions = useCallback(() => {
    // Add custom users, e.g. External Transport, unless this is a CarTransportTruck assignment
    const opts: SelectOption<number | null, ExtendedUserModel | null>[] = [];
    if (caseType !== CaseTypeEnum.CarTransportTruck && isAdmin) {
      const et = Object.values(users).find((u) => u.name === 'ET');
      const nt = Object.values(users).find((u) => u.name === 'NT');
      const autologik = Object.values(users).find(
        (u) => u.name === 'Autologik'
      );
      const nackaTransport = Object.values(users).find(
        (u) => u.name === 'Nacka Transport'
      );
      const kontoret = Object.values(users).find((u) => u.name === 'Kontoret');

      if (et) {
        opts.push({
          label: et.name,
          value: et.userID,
          extra: {
            isAvailable: true,
            user: { ...et, isActive: true } as UserModel,
          },
        });
      }

      if (nt) {
        opts.push({
          label: nt.name,
          value: nt.userID,
          extra: {
            isAvailable: true,
            user: { ...nt, isActive: true } as UserModel,
          },
        });
      }

      if (autologik) {
        opts.push({
          label: autologik.name,
          value: autologik.userID,
          extra: {
            isAvailable: true,
            user: { ...autologik, isActive: true } as UserModel,
          },
        });
      }

      if (nackaTransport) {
        opts.push({
          label: nackaTransport.name,
          value: nackaTransport.userID,
          extra: {
            isAvailable: true,
            user: { ...nackaTransport, isActive: true } as UserModel,
          },
        });
      }

      if (kontoret) {
        opts.push({
          label: kontoret.name,
          value: kontoret.userID,
          extra: {
            isAvailable: true,
            user: { ...kontoret, isActive: true } as UserModel,
          },
        });
      }
    }

    if (!filteredUsersResponse) {
      // only populate options from the useUsers hook if we don't have a filteredUsersResponse
      Object.values(users).forEach((usr) => {
        if (
          userNamesExcemptFromEstimationValidation.includes(usr.name) &&
          isAdmin
        ) {
          opts.push({
            label: usr.name,
            value: usr.userID,
            extra: {
              isAvailable: true,
              user: usr,
            },
          });
        }
      });
    } else {
      // populate options from the filteredUsersResponse
      opts.push({
        label: `Rekommenderade fälttestare (${filteredUsersResponse.sortedAvailableEmployees.length} st)`,
        value: -1,
        extra: {
          isFiller: true,
        } as ExtendedUserModel,
      });

      filteredUsersResponse.sortedAvailableEmployees
        .filter(
          (e) =>
            e.fullName !== 'ET' &&
            e.fullName !== 'NT' &&
            e.fullName !== 'Autologik' &&
            e.fullName !== 'Nacka Transport'
        )
        .forEach((esr) => {
          const usr = users[esr.id];
          if (!usr) {
            return;
          }
          opts.push({
            label: usr.name,
            description: (
              <EmojiWrapper amount={Object.keys(userPropsArray).length}>
                {getUserPropsIconArray(usr).map((e) => (
                  <div aria-label={e.title} key={e.title} title={e.title}>
                    {e.icon}
                  </div>
                ))}
              </EmojiWrapper>
            ),
            value: usr.userID,
            extra: {
              isAvailable: true,
              user: {
                ...usr,
              },
              availability: esr.schedule[0],
              warnings: esr.warnings,
              warningDetails: esr.warningDetails,
              lastDerivedEstimatedEndTime: esr.lastDerivedEstimatedEndTime,
              availableDaysInWeek: esr.availableDaysInWeek,
            } as ExtendedUserModel,
          });
        });

      opts.push({
        label: `Fälttestare med fel symboler (${filteredUsersResponse.employeesWithoutCorrectFlags.length} st)`,
        value: -2,
        extra: {
          isFiller: true,
        } as ExtendedUserModel,
      });

      filteredUsersResponse.employeesWithoutCorrectFlags
        .filter(
          (e) =>
            e.fullName !== 'ET' &&
            e.fullName !== 'NT' &&
            e.fullName !== 'Autologik' &&
            e.fullName !== 'Nacka Transport'
        )
        .forEach((esr) => {
          const usr = users[esr.id];
          if (!usr) {
            return;
          }

          opts.push({
            label: usr.name,
            description: (
              <EmojiWrapper amount={Object.keys(userPropsArray).length}>
                {getUserPropsIconArray(usr).map((e) => (
                  <div aria-label={e.title} key={e.title} title={e.title}>
                    {e.icon}
                  </div>
                ))}
              </EmojiWrapper>
            ),
            value: usr.userID,
            extra: {
              isAvailable: false,
              user: {
                ...usr,
              },
              availability: esr.schedule[0],
              warnings: esr.warnings,
              warningDetails: esr.warningDetails,
              lastDerivedEstimatedEndTime: esr.lastDerivedEstimatedEndTime,
              availableDaysInWeek: esr.availableDaysInWeek,
            } as ExtendedUserModel,
          });
        });

      opts.push({
        label: `Ovalbara fälttestare (${filteredUsersResponse.illicitToDriveEmployees.length} st)`,
        value: -3,
        extra: {
          isFiller: true,
        } as ExtendedUserModel,
      });

      filteredUsersResponse.illicitToDriveEmployees
        .filter(
          (e) =>
            e.fullName !== 'ET' &&
            e.fullName !== 'NT' &&
            e.fullName !== 'Autologik' &&
            e.fullName !== 'Nacka Transport'
        )
        .forEach((esr) => {
          const usr = users[esr.id];
          if (!usr) {
            return;
          }

          opts.push({
            label: usr.name,
            description: (
              <EmojiWrapper amount={Object.keys(userPropsArray).length}>
                {getUserPropsIconArray(usr).map((e) => (
                  <div aria-label={e.title} key={e.title} title={e.title}>
                    {e.icon}
                  </div>
                ))}
              </EmojiWrapper>
            ),
            value: usr.userID,
            extra: {
              warnings: esr.warnings,
              warningDetails: esr.warningDetails,
              isAvailable: false,
              user: {
                ...usr,
              },
              availability: esr.schedule[0],
              lastDerivedEstimatedEndTime: esr.lastDerivedEstimatedEndTime,
              availableDaysInWeek: esr.availableDaysInWeek,
            } as ExtendedUserModel,
          });
        });
    }

    opts.unshift({
      label: bookedTo ? 'Ingen vald' : 'Välj bokat till',
      value: null,
      extra: null,
    });
    setOptions(opts);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsersResponse]);

  useEffect(() => {
    if (!isRefetching && filteredUsersResponse) {
      updateOptions();
    }
  }, [isRefetching, filteredUsersResponse, updateOptions]);

  const handleOpenAvailabilityModal = useCallback(
    (option: SelectOption<number | null, ExtendedUserModel | null>) => {
      modalStack.push(
        <AvailabilityFormModal
          availability={option.extra!.availability!}
          name={option.extra!.user?.name ?? ''}
          onClose={() => modalStack.pop()}
          onSaved={() => modalStack.pop()}
          userId={option.extra?.user?.userID ?? -1}
          viewOnly
        />
      );
    },
    [modalStack]
  );

  const optionRender = useCallback(
    (
      key: string,
      option: SelectOption<number | null, ExtendedUserModel | null>,
      onClick: () => void,
      isSelected: boolean
    ) => {
      return (
        <OptionRow
          key={`${key}_row`}
          rowKey={key}
          option={option}
          onClick={onClick}
          isSelected={isSelected}
          isAdmin={isAdmin}
          availabilityStatuses={availabilityStatuses}
          availabilityStatusColorCodes={availabilityStatusColorCodes}
          absenceReasons={absenceReasons}
          absenceStatuses={absenceStatuses}
          onOpenAvailabilityModal={handleOpenAvailabilityModal}
        />
      );
    },
    [
      isAdmin,
      availabilityStatuses,
      availabilityStatusColorCodes,
      absenceReasons,
      absenceStatuses,
      handleOpenAvailabilityModal,
    ]
  );

  const isPickable = (warnings: AssigneeWarning[] | undefined) =>
    // Allow picking if the only unallowed warning is IsOnlyAvailableForFurtherTransport and the assignment type is FurtherTransport
    (assignmentType === AssignmentTypeEnum.FurtherTransport &&
      !!warnings?.every(
        (e) => e === AssigneeWarning.IsOnlyAvailableForFurtherTransport
      )) ||
    // or if the the assignment was in the past and it only has warnings that are allowed if the assignment is in the past
    (isPastAssignment &&
      !!warnings?.every((e) =>
        AllowedAssigneeWarningsOnlyIfInThePast.includes(e)
      )) ||
    // Otherwise, allow picking only if there are no unallowed warnings...
    !warnings?.some((e) =>
      // assignmentType !== AssignmentTypeEnum.FurtherTransport &&
      AlwaysUnallowedAssigneeWarnings.includes(e)
    );

  const handleItemClick = (
    _userId: number | null,
    _: boolean,
    option: SelectOption<number | null, ExtendedUserModel | null>
  ) => {
    if (option.extra?.user) {
      const { user, warnings } = option.extra;
      if (isPickable(warnings)) {
        onUserPicked(user);
        return;
      }

      // Runnerspärren är hård, men har ett nödfallsundantag: är den det ENDA som stoppar valet
      // får TL tilldela med en motivering, som servern kräver och loggar på ärendet.
      const isRunnerBlocked = !!warnings?.includes(
        AssigneeWarning.RunnerCustomerContact
      );
      if (
        allowRunnerOverride &&
        isRunnerBlocked &&
        isPickable(
          warnings?.filter((e) => e !== AssigneeWarning.RunnerCustomerContact)
        )
      ) {
        modalStack.push(
          <RunnerOverrideReasonModal
            runnerName={user.name ?? ''}
            onClose={() => modalStack.pop()}
            onConfirm={(reason) => {
              modalStack.pop();
              onUserPicked(user, undefined, reason);
            }}
          />
        );
      }
    } else if (option.value === null) {
      onUserPicked(null);
    }
  };

  if (assignmentId === -1 && requireExistingAssignmentToSelectFT) {
    return (
      <div>Du behöver skapa uppdraget innan du kan välja fälttestare.</div>
    );
  }
  if (!bookedTo) {
    return <div>Välj bokat till för att välja fälttestare.</div>;
  }

  return (
    <StyledSearchableSelect
      autoFocus={autoFocus}
      contentClassName={PrioritizedUserPickerClassName}
      disabled={disabled}
      dropdownPosition={dropdownPosition}
      itemsWrapperStyle={ItemsWrapperStyle}
      loading={isRefetching}
      maxItems={100}
      onOpenChanged={(isOpen) => {
        setPickerIsOpen(isOpen);
      }}
      onOptionClicked={handleItemClick}
      optionRender={optionRender}
      options={options}
      searchFilter={(searchString, opts) => {
        const lowerNameFilter = searchString.toLowerCase();
        return opts.filter((opt) => {
          if (opt.value === null) {
            return true;
          }
          if (opt.value < 0) {
            return true;
          }
          return (
            (opt.extra?.user?.isActive &&
              (opt.extra?.user?.name?.toLowerCase().includes(lowerNameFilter) ||
                opt.extra?.user?.city
                  ?.toLowerCase()
                  .includes(lowerNameFilter) ||
                opt.extra?.user?.zipCodeLocalArea?.name
                  .toLowerCase()
                  .includes(lowerNameFilter) ||
                getUserPropsIconArray(opt.extra.user).some(
                  (e) =>
                    e.enabled &&
                    e.title?.toLowerCase().includes(lowerNameFilter)
                ))) ||
            false
          );
        });
        // return opts;
      }}
      selectedValue={selectedUserId}
      showNonOptionSelectedName={
        selectedUserId ? users[selectedUserId]?.name : undefined
      }
    />
  );
};

export default PrioritizedCaseAssignmentUserPicker;
